import type { Ctx } from '@/core/context';
import { bus } from '@/core/bus';
import { nextRandom } from '@/core/rng';
import { bump } from './achievements';
import { gainPoints, gainXp, giveRaise, hasPerk } from './progress';
import { tr } from '@/i18n';

/**
 * v0.6 老鼠：非常少見。
 * - 平常每 rollEverySec 秒（實際時間）擲一次，機率 chance（0.4%）；生產結束後的打掃時間（下班前 cleanupFromEndMinutes 分鐘起）提高到 cleanupChance（1%）
 * - 只會出現在老鼠喜歡的地方（rats.spots）：1F 洗籃區（潮濕溫暖）、B1 倉庫（乾貨多）、2F 調理區（食物殘渣多），而且只在玩家所在的樓層
 * - 抓起來（裝袋）交給品保人員（品保小葉、品保主管 Joyce）→ 月薪 +$100／隻
 * - 老鼠雷達（福利社）：機率 ×3、小地圖標出位置；Lv 10 起機率 ×2
 */
let acc = 0;
const QA_NPCS = new Set(['qa-patroller', 'qa-manager']);
export const isQa = (npcId: string) => QA_NPCS.has(npcId);

export function cleanupTime(ctx: Ctx): boolean {
  return ctx.day.clock >= ctx.day.shiftEnd - ctx.c.game.rats.cleanupFromEndMinutes;
}

/** 每幀呼叫（dt＝實際秒數） */
export function tickRats(ctx: Ctx, dt: number, elapsed: number): void {
  const R = ctx.c.game.rats, d = ctx.day;
  if (d.rat && elapsed > d.rat.until) { ratEscaped(ctx); return; }
  if (d.rat) return;
  acc += dt; if (acc < R.rollEverySec) return; acc = 0;
  const spots = R.spots.filter(s => s.floor === d.floor); if (!spots.length) return;
  let p = cleanupTime(ctx) ? R.cleanupChance : R.chance;
  if (d.flags.ratRadar) p *= 3; else if (hasPerk(ctx, 10)) p *= 2;
  if (nextRandom(d) >= p) return;
  spawnRat(ctx, elapsed, spots[Math.floor(nextRandom(d) * spots.length)].zone);
}

export function spawnRat(ctx: Ctx, elapsed: number, zoneId: string): boolean {
  const d = ctx.day; const z = ctx.world.rt().floor.zones.find(q => q.id === zoneId); if (!z) return false;
  const pos = ctx.world.randomOpenIn(z.rect as [number, number, number, number]); if (!pos) return false;
  d.rat = { floor: d.floor, pos, until: elapsed + ctx.c.game.rats.lifeSec, zone: zoneId };
  bump(ctx, 'ratsSeen');
  const why = ctx.c.game.rats.spots.find(s => s.zone === zoneId)?.why ?? '';
  bus.emit('sfx', 'warn');
  ctx.ui.toast(tr("🐀 有老鼠！（{0}）快去抓起來交給品保——跑掉就要通報。", why));
  ctx.log(tr("看到老鼠：{0}", z.name), 'warn');
  ctx.world.refreshActors();
  return true;
}

function ratEscaped(ctx: Ctx): void {
  const d = ctx.day; if (!d.rat) return;
  d.rat = null; d.flags.ratToReport = true;
  ctx.apply({ foodSafety: -2 }, 'rat');
  ctx.ui.toast(tr("🐀 老鼠鑽進牆縫不見了……記得跟品保通報看到老鼠的地方。"));
  ctx.world.refreshActors(); ctx.save();
}

/** E 在老鼠旁邊 */
export function catchRatCard(ctx: Ctx): void {
  const d = ctx.day; if (!d.rat) return;
  ctx.ui.card({ name: tr("🐀 老鼠！"), role: tr("病媒"), color: '#8a8f94',
    text: tr("一隻灰色的小老鼠縮在角落，鬍鬚一直抖。牠隨時會跑掉！"),
    sections: [{ label: tr("提醒"), items: [tr("不要用手直接抓、不要用殺蟲劑噴在食品區。"), tr("抓到的老鼠交給品保：他們會追查從哪裡進來、哪些產品要評估。")], tone: 'info' }],
    choices: [
      { text: tr("🧤 戴上手套，用捕鼠籠罩住、裝進袋子"), tone: 'primary', fn: () => {
        ctx.advance(3, tr("抓老鼠"));
        if (nextRandom(d) < 0.75) {
          d.rat = null; d.pack.push('rat-bag'); bump(ctx, 'ratsCaught'); gainXp(ctx, 'rat', tr("抓到老鼠"));
          ctx.ui.card({ name: tr("🐀 抓到了！"), role: tr("裝在袋子裡"), color: '#3f9e6c', expression: 'happy',
            text: tr("老鼠裝進袋子、綁好了。現在拿去交給品保人員（品保小葉在現場巡、品保主管 Joyce 在 4F 品保室）。"),
            choices: [{ text: tr("好！"), tone: 'primary', fn: () => ctx.ui.close() }] });
        } else {
          d.rat = null; d.flags.ratToReport = true;
          ctx.ui.card({ name: tr("🐀 跑掉了……"), role: tr("病媒"), color: '#d9534a', expression: 'worried',
            text: tr("差一點！牠鑽進排水溝旁的縫隙不見了。跟品保通報看到的地方。"), choices: [{ text: tr("好"), fn: () => ctx.ui.close() }] });
        }
        ctx.world.refreshActors(); ctx.save();
      } },
      { text: tr("先不要碰，去找品保來"), fn: () => { d.flags.ratToReport = true; ctx.ui.close(); ctx.ui.toast(tr("記得跟品保說在哪裡看到的。")); } },
    ] });
}

/** 和品保說話時：交出老鼠 → 加薪；或通報看到老鼠 */
export function qaRatChoices(ctx: Ctx, npcName: string): { text: string; fn: () => void; tone?: 'good' }[] {
  const d = ctx.day; const out: { text: string; fn: () => void; tone?: 'good' }[] = [];
  const n = d.pack.filter(x => x === 'rat-bag').length;
  if (n) out.push({ text: tr("🐀 交出抓到的老鼠（{0} 隻）", n), tone: 'good', fn: () => {
    d.pack = d.pack.filter(x => x !== 'rat-bag');
    const R = ctx.c.game.rats;
    for (let i = 0; i < n; i++) giveRaise(ctx, R.raise, tr("抓到老鼠交給品保"));
    gainPoints(ctx, (ctx.c.game.progression.points.rat ?? 0) * n, tr("抓老鼠"));
    gainXp(ctx, 'report'); ctx.apply({ foodSafety: 3, records: 3 }, 'rat');
    bump(ctx, 'ratsDelivered', n); bus.emit('sfx', 'ok');
    ctx.ui.card({ name: npcName, role: tr("品保"), color: '#5fb38a', expression: 'happy', badge: { text: tr("💰 加薪 ${0}", R.raise * n), color: '#3f9e6c' },
      text: tr("{0}謝謝你！我會記錄抓到的地點、檢查附近的防鼠設施和產品。這個月加薪 ${1}，好好幹！", n > 1 ? tr("{0} 隻！", n) : '', R.raise * n),
      sections: [{ label: tr("現在的月薪"), items: [`$${ctx.profile.salary.toLocaleString()}`], tone: 'good' }], choices: [{ text: tr("謝謝！"), tone: 'primary', fn: () => ctx.ui.close() }] });
    ctx.save();
  } });
  else if (d.flags.ratToReport) out.push({ text: tr("🐀 通報：剛剛看到老鼠"), fn: () => {
    d.flags.ratToReport = false; gainXp(ctx, 'report'); ctx.apply({ records: 2 }, 'rat');
    ctx.ui.card({ name: npcName, role: tr("品保"), color: '#5fb38a', text: tr("謝謝通報！我會在那邊加放捕鼠籠，查一下牆縫和排水溝。下次能抓到的話，交給我會加薪喔。"), choices: [{ text: tr("好"), fn: () => ctx.ui.close() }] });
    ctx.save();
  } });
  return out;
}
